import { Badge, Button, SegmentedControl, Skeleton } from "@design";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { WikiConnections } from "./connections/WikiConnections";
import { DeletePageModal } from "./DeletePageModal";
import { isNodeType } from "./nodeTypes";
import { useWikiFile } from "./useWiki";
import { VisibilityBadge } from "./VisibilityBadge";
import { WikiBacklinks, WikiIssues } from "./WikiDocumentParts";
import { WikiEditor } from "./WikiEditor";
import { fileLabel } from "./WikiFileList";
import { WikiMarkdown } from "./WikiMarkdown";
import { stripFrontmatter } from "./wikilinks";

type View = "preview" | "source" | "connections";

/** One page: rendered or raw, editable by editors, with issues and backlinks. Key it by path. */
export function WikiDocument({
  spaceUuid,
  path,
  onClose,
}: Readonly<{ spaceUuid: string; path: string; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const file = useWikiFile(spaceUuid, path);
  const [editing, setEditing] = useState(false);
  const [view, setView] = useState<View>("preview");
  const [deleting, setDeleting] = useState(false);

  const back = (
    <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft size={16} aria-hidden />} onClick={onClose}>
      {t("allPages")}
    </Button>
  );

  if (file.isPending) return <Skeleton className="h-64 w-full" />;
  if (file.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <span className="md:hidden">{back}</span>
        <Text role="alert" tone="secondary">
          {t("document.notFound")}
        </Text>
      </div>
    );
  }

  const data = file.data;
  const title = data.node?.canonicalName ?? fileLabel({ title: null, path: data.path });
  const body = stripFrontmatter(data.content).trim();
  const updated = formatRelativeTime(data.updatedAt);

  return (
    <article aria-label={title} className="flex min-w-0 flex-col gap-4">
      <span className="md:hidden">{back}</span>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Text variant="h3" as="h2" className="break-words">
            {title}
          </Text>
          <Text variant="caption" tone="tertiary" className="break-all">
            {data.path}
          </Text>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="neutral" size="compact">
              {isNodeType(data.node?.nodeType) ? t(`type.${data.node.nodeType}`) : t("type.none")}
            </Badge>
            <VisibilityBadge visibility={data.visibility} />
            <Text variant="caption" tone="tertiary">
              {data.updatedBy
                ? t("document.updatedBy", { when: updated, who: data.updatedBy })
                : t("document.updated", { when: updated })}
            </Text>
          </div>
        </div>
        {data.canEdit && !editing && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Pencil size={16} aria-hidden />}
              onClick={() => setEditing(true)}
            >
              {t("document.edit")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Trash2 size={16} aria-hidden />}
              onClick={() => setDeleting(true)}
            >
              {t("document.delete")}
            </Button>
          </div>
        )}
      </header>

      {editing ? (
        <WikiEditor spaceUuid={spaceUuid} file={data} onDone={() => setEditing(false)} />
      ) : (
        <>
          <SegmentedControl<View>
            aria-label={t("document.view")}
            value={view}
            onChange={setView}
            options={[
              { value: "preview", label: t("document.preview") },
              { value: "source", label: t("document.source") },
              { value: "connections", label: t("document.connections") },
            ]}
          />
          {view === "source" && (
            <Text
              variant="code"
              as="pre"
              className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-surface-secondary p-4"
            >
              {data.content}
            </Text>
          )}
          {view === "connections" && (
            <WikiConnections
              page={{ spaceUuid, path: data.path, title }}
              links={data.links}
              backlinks={data.backlinks}
            />
          )}
          {view === "preview" &&
            (body ? (
              <WikiMarkdown content={data.content} links={data.links} />
            ) : (
              <Text tone="tertiary">{t("document.emptyBody")}</Text>
            ))}
        </>
      )}

      <WikiIssues diagnostics={data.diagnostics} />
      <WikiBacklinks backlinks={data.backlinks} />

      {deleting && (
        <DeletePageModal
          spaceUuid={spaceUuid}
          path={data.path}
          title={title}
          revision={data.revision}
          onClose={() => setDeleting(false)}
          onDeleted={onClose}
        />
      )}
    </article>
  );
}
